import imageCompression from 'browser-image-compression';

/**
 * Compress an image file using browser-image-compression
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options (optional)
 * @returns {Promise<File>} - The compressed image file
 */
export async function compressImage(file, options = {}) {
  // Default compression options
  const defaultOptions = {
    maxSizeMB: 0.5,             // target ~0.5 MB
    maxWidthOrHeight: 1600,     // max dimension
    useWebWorker: true,         // use web worker for better performance
    fileType: "image/webp"      // convert to WebP for better compression
  };

  // Merge with custom options
  const compressionOptions = { ...defaultOptions, ...options };

  try {    
    // Compress the image
    const compressedFile = await imageCompression(file, compressionOptions);
    
    
    return compressedFile;
  } catch (error) {
    console.error('Error compressing image:', error);
    // Return original file if compression fails
    return file;
  }
}

/**
 * Compress multiple image files
 * @param {File[]} files - Array of image files to compress
 * @param {Object} options - Compression options (optional)
 * @returns {Promise<File[]>} - Array of compressed image files
 */
export async function compressImages(files, options = {}) {
  const compressionPromises = files.map(file => compressImage(file, options));
  return Promise.all(compressionPromises);
}

/**
 * Check if a file is an image that can be compressed
 * @param {File} file - The file to check
 * @returns {boolean} - True if the file can be compressed
 */
export function canCompressImage(file) {
  const compressibleTypes = [
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'image/bmp',
    'image/tiff',
    'image/webp'
  ];
  
  return compressibleTypes.includes(file.type);
}

/**
 * Compress image with different quality settings based on file size
 * @param {File} file - The image file to compress
 * @returns {Promise<File>} - The compressed image file
 */
export async function smartCompressImage(file) {
  const fileSizeMB = file.size / 1024 / 1024;
  
  let options = {};
  
  if (fileSizeMB > 10) {
    // Very large files - aggressive compression
    options = {
      maxSizeMB: 0.3,
      maxWidthOrHeight: 1200,
      initialQuality: 0.6,
      fileType: "image/webp"
    };
  } else if (fileSizeMB > 5) {
    // Large files - moderate compression
    options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 1400,
      initialQuality: 0.7,
      fileType: "image/webp"
    };
  } else if (fileSizeMB > 2) {
    // Medium files - light compression
    options = {
      maxSizeMB: 0.8,
      maxWidthOrHeight: 1600,
      initialQuality: 0.8,
      fileType: "image/webp"
    };
  } else {
    // Small files - minimal compression, preserve quality
    options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1800,
      initialQuality: 0.9,
      fileType: "image/webp"
    };
  }
  
  return compressImage(file, options);
}
